import React, { useState } from "react";
import { Modal, Box, Typography, TextField, Button } from "@mui/material";

const RequestModal = ({
  isOpen,
  onClose,
  title,
  handleSubmit,
  groupedData,
  data,
}) => {
  const [requestAmount, setRequestAmount] = useState("");
  const [error, setError] = useState("");

  const getRemainingBalance = () => {
    // restrict_amount is a single top-level field on the booking entry (same value
    // for every request under this booking+vendor), not repeated per trip_advances[] item.
    const restrictAmount = parseFloat(data?.restrict_amount) || 0;
    return Math.max(0, restrictAmount);
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;

    // Prevent leading zeros
    if (value.startsWith("0")) value = value.replace(/^0+/, "");

    setRequestAmount(value);

    const tripAdvanceRequest = parseFloat(value) || 0;
    const remaining = getRemainingBalance();

    if (!value || tripAdvanceRequest === 0) {
      setError("Value cannot be zero. Please enter a valid amount.");
      return;
    }

    if (tripAdvanceRequest > remaining) {
      setError(
        `Unable to send request. You can request up to ${remaining} only.`,
      );
      return;
    }

    setError("");
  };

  const onSubmit = () => {
    const tripAdvanceRequest = parseFloat(requestAmount) || 0;
    const remaining = getRemainingBalance();

    if (tripAdvanceRequest <= 0) {
      setError("Value cannot be zero. Please enter a valid amount.");
      return;
    }

    if (tripAdvanceRequest > remaining) {
      setError(
        `Unable to send request. You can request up to ${remaining} only.`,
      );
      return;
    }

    setError("");
    handleSubmit(data, tripAdvanceRequest);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        setRequestAmount("");
        setError("");
        onClose();
      }}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          width: "400px",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <Typography variant="h6" id="modal-title" align="center">
            {title}
          </Typography>
        </div>

        {/* Close Button */}
        <button
          onClick={() => {
            setRequestAmount("");
            setError("");
            onClose();
          }}
          className="text-gray-500 hover:text-gray-800 transition duration-200"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Modal Body */}

        <Typography
          sx={{
            mb: 2,
            fontSize: "12px",
            color: "text.secondary",
          }}
        >
          You can request up to <strong>{getRemainingBalance()}</strong> only.
        </Typography>
        <TextField
          type="number"
          value={requestAmount}
          onChange={handleAmountChange}
          label="Enter Amount"
          fullWidth
          variant="outlined"
          error={Boolean(error)}
          helperText={error}
          sx={{ marginBottom: 2 }}
        />

        {/* Modal Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="text"
            color="error"
            onClick={() => {
              setRequestAmount("");
              setError("");
              onClose();
            }}
            sx={{ marginRight: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={onSubmit}
            disabled={
              !requestAmount || parseFloat(requestAmount) <= 0 || Boolean(error)
            }
            sx={{
              color: "green",
              marginRight: 2,
            }}
          >
            Request
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RequestModal;
